import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Theme,
  Tooltip,
  Typography,
} from "@mui/material";
import { makeStyles } from "helpers/StyleHelper";
import { useTheme } from "@mui/material/styles";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useState } from "react";
import { FormattedMessage as T, useIntl } from "react-intl";
import {
  CertificateRequest,
  ICertificate,
} from "services/KnowfullyAdminClient";
import { CommonFormProps } from "types/common-types";
import CertificateForm from "./CertificateForm";
import CertificateSkusList from "./CertificateSkusList";
import CertificateProductsList from "./CertificateProductsList";

const useStyles = makeStyles((theme: Theme) => ({
  expandedPanel: {
    backgroundColor: theme.palette.secondary.light,
  },
}));

export type CertificateAccordionsProps = {
  certificateId?: string | undefined;
} & CommonFormProps<ICertificate, CertificateRequest>;

const CertificateAccordions = ({
  certificateId,
  entity: certificate,
  onSubmit,
  onCancel,
  submitLabel,
}: CertificateAccordionsProps) => {
  const styles = useStyles(useTheme());
  const { formatMessage: t } = useIntl();
  const [expandedSection, setExpandedSection] = useState<string | null>(
    "details"
  );

  const locked = certificateId === undefined;

  const accordionProps = (section: string) => {
    return {
      square: true,
      expanded: expandedSection === section,
      onChange: toggleExpansion(section),
      disabled: locked && expandedSection !== section,
    };
  };

  const toggleExpansion = (section: string) => (e: any, expanded: boolean) => {
    if (locked || !certificateId) return;
    setExpandedSection(expanded ? section : null);
  };

  return (
    <>
      <Accordion {...accordionProps("details")}>
        <AccordionSummary
          style={{ margin: 0 }}
          expandIcon={<ExpandMoreIcon />}
          sx={accordionProps("details").expanded ? styles.expandedPanel : null}
          data-cy="accordionCertificateButton"
        >
          <Typography>
            <T id="detailsLabel" />
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <CertificateForm
            onSubmit={onSubmit}
            onCancel={onCancel}
            entity={certificate}
            submitLabel={submitLabel}
          />
        </AccordionDetails>
      </Accordion>
      <Tooltip
        title={locked ? t({ id: "accordionDisabledToCreationMessage" }) : ""}
      >
        <Accordion {...accordionProps("skus")}>
          <AccordionSummary
            style={{ margin: 0 }}
            expandIcon={<ExpandMoreIcon />}
            sx={accordionProps("skus").expanded ? styles.expandedPanel : null}
            data-cy="accordionCertificateSkusButton"
          >
            <Typography>
              <T id="certificateSkuFormTitle" />
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <CertificateSkusList certificateId={certificateId!} />
          </AccordionDetails>
        </Accordion>
      </Tooltip>
      <Tooltip
        title={locked ? t({ id: "accordionDisabledToCreationMessage" }) : ""}
      >
        <Accordion {...accordionProps("products")}>
          <AccordionSummary
            style={{ margin: 0 }}
            expandIcon={<ExpandMoreIcon />}
            sx={accordionProps("products").expanded ? styles.expandedPanel : null}
            data-cy="accordionCertificateProductsButton"
          >
            <Typography>
              <T id="certificateProductsTitle" />
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <CertificateProductsList certificateId={certificateId!} />
          </AccordionDetails>
        </Accordion>
      </Tooltip>
    </>
  );
};

export default CertificateAccordions;
